import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonSpinner,
  IonText,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSegment,
  IonSegmentButton,
  IonModal,
  IonIcon,
  IonProgressBar,
  IonToggle,
  IonInput,
  IonNote,
} from '@ionic/react';
import { sparklesOutline, closeOutline, refreshOutline, flashOutline, alertCircleOutline, timeOutline, chatbubblesOutline } from 'ionicons/icons';
import { useAdminAi } from '../../../application/use-cases/admin/useAdminAi';
import AdminRowIcon from '../../components/admin/AdminRowIcon';
import AdminListFooter from '../../components/admin/AdminListFooter';
import type { AdminAiFeature, AdminAiTotals, AdminAiTraceDetail, AdminAiTraceStatus } from '../../../domain/Admin';
import './Admin.css';

const featureLabels: Record<AdminAiFeature, string> = {
  chat: 'Chat',
  field_analysis: 'Análisis del campo',
  daily_summary: 'Resumen diario',
};

const statusLabels: Record<AdminAiTraceStatus, string> = {
  ok: 'OK',
  error: 'Error',
  rate_limited: 'Límite',
  aborted: 'Detenida',
};

const statusColor: Record<AdminAiTraceStatus, string> = {
  ok: 'success',
  error: 'danger',
  rate_limited: 'warning',
  aborted: 'medium',
};

const fmt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString('es-AR'));
// 200000 → "200K", para los límites.
const short = (n: number) => (n >= 1000 ? `${(n / 1000).toLocaleString('es-AR', { maximumFractionDigits: 1 })}K` : String(n));
const seconds = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} s`);
const tokensOf = (t: Pick<AdminAiTotals, 'inputTokens' | 'outputTokens'>) => t.inputTokens + t.outputTokens;

// Lo que quedó escrito en un campo numérico; null si está vacío o no es un número (no se guarda).
const intValue = (value: string | number | null | undefined) => {
  const n = typeof value === 'number' ? value : parseInt(value ?? '', 10);
  return Number.isInteger(n) ? n : null;
};

const modeLabels: Record<string, string> = { full: 'Completo', lean: 'Económico' };

const roleLabels: Record<string, string> = { system: 'Sistema', user: 'Usuario', assistant: 'Asistente', tool: 'Resultado de herramienta' };

// Fila de totales (un día, una función, una versión del prompt).
const TotalsMeta: React.FC<{ totals: AdminAiTotals }> = ({ totals }) => (
  <p className="admin-list-row-meta">
    {fmt(totals.requests)} llamadas · {fmt(tokensOf(totals))} tokens · prom. {fmt(totals.avgInputTokens)} de entrada · {seconds(totals.avgLatencyMs)}
    {totals.errors + totals.rateLimited > 0 ? ` · ${totals.errors} errores, ${totals.rateLimited} por límite` : ''}
  </p>
);

const TraceDetail: React.FC<{ trace: AdminAiTraceDetail }> = ({ trace }) => {
  const [showSystem, setShowSystem] = useState(false);
  return (
    <>
      <div className="admin-ai-detail-meta">
        <span>{new Date(trace.createdAt).toLocaleString('es-AR')}</span>
        <span>{featureLabels[trace.feature]}{trace.round > 0 ? ` · vuelta ${trace.round + 1}` : ''}</span>
        <span>{trace.userEmail ?? 'usuario borrado'}</span>
        <span>
          {trace.provider} · {trace.model}
        </span>
        <span>
          {fmt(trace.inputTokens)} tokens de entrada{trace.cachedTokens ? ` (${fmt(trace.cachedTokens)} de caché)` : ''} · {fmt(trace.outputTokens)} de
          salida{trace.reasoningTokens ? ` (${fmt(trace.reasoningTokens)} pensando)` : ''}
        </span>
        <span>
          Primera respuesta {seconds(trace.firstTokenMs)} · total {seconds(trace.latencyMs)}
        </span>
        {trace.promptMode && (
          <span>
            Prompt {modeLabels[trace.promptMode] ?? trace.promptMode}
            {trace.promptBlocks.length > 0 ? `: ${trace.promptBlocks.join(', ')}` : ''}
          </span>
        )}
        <span>
          <IonBadge color={statusColor[trace.status]}>{statusLabels[trace.status]}</IonBadge>
          {trace.httpStatus ? ` HTTP ${trace.httpStatus}` : ''}
        </span>
        {trace.tools.length > 0 && <span>Herramientas ofrecidas: {trace.tools.join(', ')}</span>}
      </div>

      {trace.systemPrompt && (
        <section className="admin-ai-block">
          <h3>
            Prompt de sistema <code>{trace.promptHash}</code>
            <IonButton size="small" fill="clear" onClick={() => setShowSystem((v) => !v)}>
              {showSystem ? 'Ocultar' : `Ver (${fmt(trace.systemPrompt.length)} caracteres)`}
            </IonButton>
          </h3>
          {showSystem && <pre className="admin-ai-pre">{trace.systemPrompt}</pre>}
        </section>
      )}

      {trace.messages.map((m, i) => (
        <section key={i} className="admin-ai-block">
          <h3>
            {roleLabels[m.role] ?? m.role} <small>{fmt(m.content.length)} caracteres</small>
          </h3>
          {m.content && <pre className="admin-ai-pre">{m.content}</pre>}
          {m.role === 'assistant' &&
            m.toolCalls?.map((c) => (
              <pre key={c.id} className="admin-ai-pre">
                → {c.name}({c.arguments})
              </pre>
            ))}
        </section>
      ))}

      <section className="admin-ai-block">
        <h3>
          Respuesta <small>{fmt(trace.output.length)} caracteres{trace.reasoningChars ? ` · pensó ${fmt(trace.reasoningChars)}` : ''}</small>
        </h3>
        {trace.output ? <pre className="admin-ai-pre">{trace.output}</pre> : <p className="admin-list-row-meta">Sin texto.</p>}
        {trace.toolCalls.map((c) => (
          <pre key={c.id} className="admin-ai-pre">
            → {c.name}({c.arguments})
          </pre>
        ))}
      </section>
    </>
  );
};

const AdminAi: React.FC = () => {
  const [tab, setTab] = useState<'usage' | 'traces' | 'settings'>('usage');
  const ai = useAdminAi();
  const { usage } = ai;
  const tokensToday = usage ? tokensOf(usage.today) : 0;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/admin" />
          </IonButtons>
          <IonTitle>Asistente de IA</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={ai.refresh} aria-label="Actualizar">
              <IonIcon icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as 'usage' | 'traces' | 'settings')}>
            <IonSegmentButton value="usage">
              <IonLabel>Uso</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="traces">
              <IonLabel>Llamadas ({fmt(ai.total)})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="settings">
              <IonLabel>Ajustes</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
        {tab === 'traces' && (
          <IonToolbar>
            <IonSearchbar value={ai.query} placeholder="Buscar por email" onIonInput={(e) => ai.setQuery(e.detail.value ?? '')} />
          </IonToolbar>
        )}
        {tab === 'traces' && (
          <IonToolbar>
            <div className="admin-ai-filters">
              <IonSelect
                aria-label="Función"
                interface="popover"
                value={ai.feature ?? ''}
                onIonChange={(e) => ai.setFeature(e.detail.value || null)}
                className="admin-ai-filter"
              >
                <IonSelectOption value="">Todas las funciones</IonSelectOption>
                {Object.entries(featureLabels).map(([value, label]) => (
                  <IonSelectOption key={value} value={value}>
                    {label}
                  </IonSelectOption>
                ))}
              </IonSelect>
              <IonSelect
                aria-label="Estado"
                interface="popover"
                value={ai.status ?? ''}
                onIonChange={(e) => ai.setStatus(e.detail.value || null)}
                className="admin-ai-filter"
              >
                <IonSelectOption value="">Todos los estados</IonSelectOption>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <IonSelectOption key={value} value={value}>
                    {label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>
          </IonToolbar>
        )}
      </IonHeader>

      <IonContent fullscreen className={tab === 'traces' ? undefined : 'ion-padding admin-ai-panel'}>
        {ai.usageError && (
          <IonText color="danger">
            <p className="ion-padding">{ai.usageError}</p>
          </IonText>
        )}

        {tab === 'usage' && !usage && !ai.usageError && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}

        {tab === 'usage' && usage && (
          <>
            <p className="admin-list-row-meta">
              {usage.provider} · {usage.model}
              {ai.settings &&
                ` · prompt ${modeLabels[ai.settings.promptMode].toLowerCase()}, herramientas ${ai.settings.toolsEnabled ? 'prendidas' : 'apagadas'}${
                  ai.settings.experimentScope === 'admins' && (ai.settings.promptMode === 'lean' || ai.settings.toolsEnabled) ? ' (solo admins)' : ''
                }`}
            </p>
            <div className="admin-stats-grid">
              {/* El proveedor corta por tokens de entrada + salida en el día. */}
              <div className="admin-stat-tile tone-violet">
                <IonIcon icon={sparklesOutline} />
                <span className="admin-stat-value">
                  {short(tokensToday)}
                  <small> de {short(usage.limits.tokensPerDay)}</small>
                </span>
                <span className="admin-stat-label">Tokens hoy</span>
                <IonProgressBar
                  value={Math.min(tokensToday / usage.limits.tokensPerDay, 1)}
                  color={tokensToday / usage.limits.tokensPerDay > 0.8 ? 'danger' : 'primary'}
                />
              </div>
              <div className="admin-stat-tile tone-blue">
                <IonIcon icon={chatbubblesOutline} />
                <span className="admin-stat-value">
                  {fmt(usage.today.requests)}
                  <small> de {short(usage.limits.requestsPerDay)}</small>
                </span>
                <span className="admin-stat-label">Llamadas hoy</span>
              </div>
              <div className="admin-stat-tile tone-orange">
                <IonIcon icon={flashOutline} />
                <span className="admin-stat-value">{fmt(usage.today.avgInputTokens)}</span>
                <span className="admin-stat-label">Tokens de entrada por llamada</span>
                <span className="admin-stat-detail">El límite por minuto es {short(usage.limits.tokensPerMinute)}</span>
              </div>
              <div className="admin-stat-tile tone-red">
                <IonIcon icon={alertCircleOutline} />
                <span className="admin-stat-value">{fmt(usage.today.errors + usage.today.rateLimited)}</span>
                <span className="admin-stat-label">Fallas hoy</span>
                <span className="admin-stat-detail">{usage.today.rateLimited} por límite de uso</span>
              </div>
              <div className="admin-stat-tile tone-green">
                <IonIcon icon={timeOutline} />
                <span className="admin-stat-value">{seconds(usage.today.avgLatencyMs)}</span>
                <span className="admin-stat-label">Respuesta promedio hoy</span>
                <span className="admin-stat-detail">Última hora: {fmt(tokensOf(usage.lastHour))} tokens</span>
              </div>
            </div>

            {usage.byPromptMode.length > 0 && (
              <>
                <p className="settings-eyebrow">Completo vs económico (chat, 7 días)</p>
                <IonList className="admin-list">
                  {usage.byPromptMode.map((m) => (
                    <IonItem key={m.promptMode ?? 'none'} lines="full">
                      <IonLabel>
                        <h2>{m.promptMode ? modeLabels[m.promptMode] ?? m.promptMode : 'Antes de los modos'}</h2>
                        <TotalsMeta totals={m} />
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}

            <p className="settings-eyebrow">Versiones del prompt (7 días)</p>
            <p className="admin-list-row-meta">Cada cambio al prompt de sistema es una versión nueva: compará cuánto gasta y cuánto tarda.</p>
            <IonList className="admin-list">
              {usage.byPrompt.map((p) => (
                <IonItem key={p.promptHash ?? 'none'} lines="full">
                  <IonLabel>
                    <h2>
                      <code>{p.promptHash ?? 'sin prompt'}</code>
                    </h2>
                    {p.firstSeenAt && <p>Desde {new Date(p.firstSeenAt).toLocaleString('es-AR')}</p>}
                    <TotalsMeta totals={p} />
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>

            <p className="settings-eyebrow">Por función (7 días)</p>
            <IonList className="admin-list">
              {usage.byFeature.map((f) => (
                <IonItem key={f.feature} lines="full">
                  <IonLabel>
                    <h2>{featureLabels[f.feature] ?? f.feature}</h2>
                    <TotalsMeta totals={f} />
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>

            <p className="settings-eyebrow">Por día</p>
            <IonList className="admin-list">
              {[...usage.byDay].reverse().map((d) => (
                <IonItem key={d.date} lines="full">
                  <IonLabel>
                    <h2>{new Date(`${d.date}T12:00:00`).toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</h2>
                    <TotalsMeta totals={d} />
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
            {usage.byDay.length === 0 && (
              <IonText color="medium">
                <p>Todavía no se llamó a la IA en estos días.</p>
              </IonText>
            )}
          </>
        )}

        {tab === 'traces' && (
          <>
            {ai.loading && (
              <div className="ion-text-center ion-padding">
                <IonSpinner color="primary" />
              </div>
            )}
            {ai.error && (
              <IonText color="danger">
                <p className="ion-padding">{ai.error}</p>
              </IonText>
            )}
            {!ai.loading && ai.items.length === 0 && !ai.error && (
              <IonText color="medium">
                <p className="ion-padding">No hay llamadas con estos filtros.</p>
              </IonText>
            )}
            <IonList className="admin-list">
              {ai.items.map((t) => (
                <IonItem key={t.id} lines="full" button detail={false} onClick={() => ai.open(t.id)}>
                  <AdminRowIcon icon={sparklesOutline} tone={t.status === 'ok' ? 'violet' : t.status === 'aborted' ? 'grey' : 'red'} />
                  <IonLabel>
                    <h2>
                      {featureLabels[t.feature]}
                      {t.round > 0 ? ` · vuelta ${t.round + 1}` : ''}
                      {t.toolCalls.length > 0 ? ` · pidió ${t.toolCalls.map((c) => c.name).join(', ')}` : ''}
                    </h2>
                    <p>{t.userEmail ?? 'usuario borrado'}</p>
                    <p className="admin-list-row-meta">
                      {new Date(t.createdAt).toLocaleString('es-AR')} · {fmt(t.inputTokens)} → {fmt(t.outputTokens)} tokens · {seconds(t.latencyMs)} · {t.model}
                    </p>
                  </IonLabel>
                  {ai.opening === t.id ? <IonSpinner name="dots" /> : <IonBadge color={statusColor[t.status]}>{statusLabels[t.status]}</IonBadge>}
                </IonItem>
              ))}
            </IonList>
            {!ai.loading && <AdminListFooter shown={ai.items.length} total={ai.total} loadingMore={ai.loadingMore} onMore={ai.loadMore} />}
          </>
        )}

        {tab === 'settings' && !ai.settings && !ai.settingsError && (
          <div className="ion-text-center">
            <IonSpinner color="primary" />
          </div>
        )}
        {tab === 'settings' && ai.settingsError && (
          <IonText color="danger">
            <p>{ai.settingsError}</p>
          </IonText>
        )}
        {tab === 'settings' && ai.settings && (
          <>
            <p className="admin-list-row-meta">Los cambios se guardan al momento y se aplican desde la próxima pregunta.</p>

            <p className="settings-eyebrow">Experimentos</p>
            <IonList className="admin-list">
              <IonItem lines="full">
                <IonLabel>
                  <h2>Prompt de sistema</h2>
                  <p className="admin-list-row-meta">
                    Completo manda todas las instrucciones. Económico manda las de pulverización, heladas, tiempo y gráficos solo si la pregunta
                    habla de eso: gasta menos tokens.
                  </p>
                </IonLabel>
              </IonItem>
              <IonItem lines="full">
                <IonSegment
                  value={ai.settings.promptMode}
                  disabled={ai.savingSettings}
                  onIonChange={(e) => ai.saveSettings({ promptMode: e.detail.value as 'full' | 'lean' })}
                >
                  <IonSegmentButton value="full">
                    <IonLabel>Completo</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="lean">
                    <IonLabel>Económico</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
              </IonItem>
              <IonItem lines="full">
                <IonToggle
                  checked={ai.settings.toolsEnabled}
                  disabled={ai.savingSettings}
                  onIonChange={(e) => ai.saveSettings({ toolsEnabled: e.detail.checked })}
                >
                  <IonLabel>
                    <h2>Herramientas del chat</h2>
                    <p className="admin-list-row-meta">
                      El modelo pide el historial de la semana o el mes, grados día, riego y alertas. Cada pedido es otra vuelta y en el plan gratis
                      suele pasar el límite por minuto.
                    </p>
                  </IonLabel>
                </IonToggle>
              </IonItem>
              <IonItem lines="full">
                <IonSelect
                  label="Aplicar a"
                  interface="popover"
                  value={ai.settings.experimentScope}
                  disabled={ai.savingSettings}
                  onIonChange={(e) => ai.saveSettings({ experimentScope: e.detail.value })}
                >
                  <IonSelectOption value="admins">Solo admins</IonSelectOption>
                  <IonSelectOption value="all">Todos los usuarios</IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonList>
            <IonNote className="admin-ai-note">
              Con «Solo admins», el resto de los usuarios sigue con el prompt completo y sin herramientas.
            </IonNote>

            <p className="settings-eyebrow">Límites</p>
            <IonList className="admin-list">
              <IonItem lines="full">
                <IonInput
                  type="number"
                  label="Mensajes de historial que se mandan"
                  labelPlacement="stacked"
                  min={0}
                  max={20}
                  value={ai.settings.historyLimit}
                  disabled={ai.savingSettings}
                  helperText="De 0 a 20. Menos historial gasta menos, pero entiende peor las preguntas de seguimiento."
                  onIonChange={(e) => {
                    const n = intValue(e.detail.value);
                    if (n !== null && n !== ai.settings?.historyLimit) ai.saveSettings({ historyLimit: n });
                  }}
                />
              </IonItem>
              <IonItem lines="full">
                <IonInput
                  type="number"
                  label="Consultas por usuario por día"
                  labelPlacement="stacked"
                  min={0}
                  max={500}
                  value={ai.settings.dailyQueryLimit}
                  disabled={ai.savingSettings}
                  helperText={usage ? `El plan permite ${short(usage.limits.requestsPerDay)} llamadas y ${short(usage.limits.tokensPerDay)} tokens por día entre todos.` : undefined}
                  onIonChange={(e) => {
                    const n = intValue(e.detail.value);
                    if (n !== null && n !== ai.settings?.dailyQueryLimit) ai.saveSettings({ dailyQueryLimit: n });
                  }}
                />
              </IonItem>
            </IonList>
          </>
        )}

        <IonModal isOpen={!!ai.selected} onDidDismiss={ai.close}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Llamada a la IA</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={ai.close} aria-label="Cerrar">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">{ai.selected && <TraceDetail trace={ai.selected} />}</IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default AdminAi;
